<template>
  <div
    v-if="currentStep"
    :data-qa-id="currentStep.qaId ? `${currentStep.qaId}.overlay` : null"
    class="z-10000 fixed inset-0"
    @click.self="finish"
  >
    <div class="absolute" :style="highlightStyles"></div>

    <div
      ref="tooltipEl"
      :data-qa-id="currentStep.qaId ? `${currentStep.qaId}.container` : null"
      class="bg-background-neutral-onblock absolute flex items-start rounded-2xl p-4"
      :style="tooltipStyles"
    >
      <!-- prettier-ignore -->
      <svg
        width="20"
        height="8"
        class="text-background-neutral-onblock absolute"
        :class="tooltipPlacement === 'bottom' ? '-top-2' : '-bottom-2 rotate-180'"
        :style="{ left: arrowLeftOffset + 'px' }"
        viewBox="0 0 20 8"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M10 -8.74228e-07C7 -1.1365e-06 4.0001 8 -6.99382e-07 8L20 8C16.0251 8 13 -6.11959e-07 10 -8.74228e-07Z"
          fill="currentColor"
        />
      </svg>
      <div>
        <p class="text-text-neutral-primary text-sm">
          {{ currentStep.title }}
        </p>

        <div class="flex w-full justify-end">
          <button
            v-if="currentStep.action"
            type="button"
            :data-qa-id="
              currentStep.qaId ? `${currentStep.qaId}.actionButton` : null
            "
            class="text-text-positive-primary mt-2 text-sm font-semibold"
            @click="handleActionClick(currentStep.action.callback)"
          >
            {{ currentStep.action.text }}
          </button>
        </div>
      </div>
      <button
        type="button"
        :data-qa-id="
          currentStep.qaId ? `${currentStep.qaId}.closeButton` : null
        "
        @click="finish"
      >
        <LineCloseSmallIcon class="text-situational-darkgrey h-5 w-5" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onMounted,
    onUnmounted,
    ref,
    watch,
    type StyleValue,
  } from 'vue';
  import { lock, unlock, clearBodyLocks } from 'tua-body-scroll-lock';
  import { merge, toSafeInteger } from 'lodash-es';

  import LineCloseSmallIcon from '@/assets/images/line-close-small-icon.svg?component';

  interface OnboardingStep {
    targetSelector: string;
    qaId?: string;
    targetStyles?: {
      borderRadius?: string;
    };
    tooltipIndents?: {
      top?: number;
      left?: number;
    };
    tooltipStyles?: {
      width?: string;
    };
    title: string;
    action?: {
      text: string;
      callback: () => void;
    };
    onShowCallback?: () => void;
  }

  const DEFAULT_STEP_CONFIG: Omit<
    OnboardingStep,
    'targetSelector' | 'title' | 'description' | 'action'
  > = {
    tooltipIndents: {
      top: 10,
      left: 32,
    },
    tooltipStyles: {
      width: '286px',
    },
  };

  const target = ref<Element | null>(null);
  const targetRect = ref<DOMRect>();
  const currentStep = ref<OnboardingStep>();
  const tooltipEl = ref<HTMLElement | null>(null);
  const tooltipPlacement = ref<'bottom' | 'top'>('bottom');
  const isScrolling = ref(false);
  const scrollingTimeout = ref<ReturnType<typeof setTimeout>>();

  const highlightStyles = computed<StyleValue>(() => {
    if (!targetRect.value || !currentStep.value) {
      return;
    }

    /**
     * Prefer an explicit `targetStyles.borderRadius` so callers can match
     * the highlight cutout to the underlying control (e.g. `rounded-2xl`
     * = 16px for the multi-trip toggle). Fall back to the historical
     * dynamic radius — small icon buttons rely on it (favorites star).
     */
    const borderRadius =
      currentStep.value.targetStyles?.borderRadius ??
      `${targetRect.value.width / 3}px`;

    return {
      top: `${targetRect.value.top}px`,
      left: `${targetRect.value.left}px`,
      width: `${targetRect.value.width}px`,
      height: `${targetRect.value.height}px`,
      borderRadius,
      boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)',
    };
  });

  const TOOLTIP_MARGIN = 8;

  const clampedTooltipLeft = computed(() => {
    if (!targetRect.value || !currentStep.value) return 0;
    const rawLeft =
      targetRect.value.left +
      toSafeInteger(currentStep.value.tooltipIndents?.left);
    const tooltipWidth = parseInt(
      currentStep.value.tooltipStyles?.width ?? '286',
      10,
    );
    const maxLeft = window.innerWidth - tooltipWidth - TOOLTIP_MARGIN;
    return Math.max(TOOLTIP_MARGIN, Math.min(rawLeft, maxLeft));
  });

  const arrowLeftOffset = computed(() => {
    if (!targetRect.value || !currentStep.value) return 40;
    const targetCenter =
      targetRect.value.left + targetRect.value.width / 2 - 10;
    const tooltipWidth = parseInt(
      currentStep.value.tooltipStyles?.width ?? '286',
      10,
    );
    return Math.max(
      12,
      Math.min(targetCenter - clampedTooltipLeft.value, tooltipWidth - 32),
    );
  });

  const tooltipStyles = computed<StyleValue>(() => {
    if (!targetRect.value || !currentStep.value) {
      return;
    }

    const indent = toSafeInteger(currentStep.value.tooltipIndents?.top);
    const base = {
      left: `${clampedTooltipLeft.value}px`,
      width: currentStep.value.tooltipStyles?.width ?? 'fit-content',
    };

    if (tooltipPlacement.value === 'bottom') {
      return {
        ...base,
        top: `${targetRect.value.top + targetRect.value.height + indent + 8}px`,
        transform: 'none',
      };
    }

    return {
      ...base,
      top: `${targetRect.value.top - indent - 8}px`,
      transform: 'translateY(-100%)',
    };
  });

  function updateTooltipPlacement() {
    if (!targetRect.value || !currentStep.value || !tooltipEl.value) {
      return;
    }

    const indent = toSafeInteger(currentStep.value.tooltipIndents?.top);
    const tooltipHeight = tooltipEl.value.getBoundingClientRect().height;
    const bottomEdge = targetRect.value.bottom + indent + 8 + tooltipHeight;

    tooltipPlacement.value =
      bottomEdge > window.innerHeight - TOOLTIP_MARGIN ? 'top' : 'bottom';
  }

  watch([currentStep, targetRect], async () => {
    if (!currentStep.value || !targetRect.value) {
      return;
    }
    await nextTick();
    requestAnimationFrame(() => {
      updateTooltipPlacement();
    });
  });

  function onBeforeOnboardingScroll() {
    clearInterval(scrollingTimeout.value);
    isScrolling.value = true;
    scrollingTimeout.value = setTimeout(() => (isScrolling.value = false), 100);
  }

  function recalculateTargetRect() {
    targetRect.value = target.value?.getBoundingClientRect();
  }

  function getClosestTopElement(selector: string): Element | null {
    const elements = document.querySelectorAll(selector);

    let closestElement: Element | null = null;
    let minDistance = Infinity;

    elements.forEach(element => {
      const rect = element.getBoundingClientRect();

      if (Math.abs(rect.top) < minDistance) {
        minDistance = Math.abs(rect.top);
        closestElement = element;
      }
    });

    return closestElement;
  }

  function start(step: OnboardingStep) {
    function _start() {
      document.removeEventListener('scroll', onBeforeOnboardingScroll);
      document.removeEventListener('scrollend', _start);
      target.value = getClosestTopElement(step.targetSelector);
      if (target.value instanceof HTMLElement) {
        recalculateTargetRect();
        document.addEventListener('scroll', recalculateTargetRect);
        currentStep.value = merge(DEFAULT_STEP_CONFIG, step);
        step.onShowCallback?.();
      }
    }

    if (isScrolling.value) {
      document.addEventListener('scrollend', _start);
    } else {
      _start();
    }
  }

  function finish() {
    currentStep.value = undefined;
    target.value = null;
    targetRect.value = undefined;
    tooltipPlacement.value = 'bottom';
  }

  function handleActionClick(callback: () => void) {
    finish();
    callback();
  }

  function cleanup() {
    document.removeEventListener('scroll', recalculateTargetRect);
    document.removeEventListener('scroll', onBeforeOnboardingScroll);
  }

  watch(currentStep, (newValue, oldValue) => {
    if (newValue) {
      tooltipPlacement.value = 'bottom';
      const target = document.querySelector(newValue.targetSelector);
      lock(target as HTMLElement);
    } else if (oldValue) {
      const target = document.querySelector(oldValue?.targetSelector);
      unlock(target as HTMLElement);
      cleanup();
    } else {
      clearBodyLocks();
      cleanup();
    }
  });

  onMounted(() => {
    document.addEventListener('scroll', onBeforeOnboardingScroll);
  });

  onUnmounted(() => {
    if (currentStep.value) {
      const target = document.querySelector(currentStep.value.targetSelector);
      unlock(target as HTMLElement);
    } else {
      clearBodyLocks();
    }
    cleanup();
  });

  defineExpose({ start });
</script>
